import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  AfterViewInit,
  ViewChild,
  ElementRef,
  HostListener,
  ChangeDetectionStrategy,
  signal,
  computed,
  OnChanges,
  SimpleChanges,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  trigger,
  state,
  style,
  transition,
  animate,
} from '@angular/animations';
import { MatBottomSheetRef } from '@angular/material/bottom-sheet';
import {
  DropdownComponent,
  type DropdownOption,
} from '../ui/dropdown/dropdown.component';
import { IconComponent, type IconName } from '../../icons/icon.component';
import { IconComponentV2 } from '../icon-component/icon.component';
import { ClickOutsideDirective } from '../../directives/click-outside.directive';
import { BreakpointService } from '../../../core/services/breakpoint.service';

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';

// Initialize dayjs plugins
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

// Set default timezone to Los Angeles
const LA_TIMEZONE = 'America/Los_Angeles';

// Helper function to get current time in LA timezone
const nowInLA = () => dayjs().tz(LA_TIMEZONE);

export type TimeUnit = 'hour' | 'day' | 'week' | 'month' | 'quarter' | 'year';
export type ComparisonType =
  | 'none'
  | 'previous-period'
  | 'previous-month'
  | 'previous-year'
  | 'custom';
export type DateRangePreset =
  | 'today'
  | 'yesterday'
  | 'last7days'
  | 'last14days'
  | 'last30days'
  | 'last3Months'
  | 'last90days'
  | 'thisWeek'
  | 'lastWeek'
  | 'thisMonth'
  | 'lastMonth'
  | 'thisQuarter'
  | 'lastQuarter'
  | 'thisYear'
  | 'lastYear'
  | 'custom';

export interface DateRange {
  start: string; // ISO date string in UTC
  end: string; // ISO date string in UTC
}

export interface DateRangePickerValue {
  startDate: string;
  endDate: string;
  unit: TimeUnit;
  comparisonStartDate?: string;
  comparisonEndDate?: string;
  comparisonType: ComparisonType;
  presetLabel?: string;
}

export interface DateRangePickerOutput {
  start_date: string;
  end_date: string;
  previous_start_date: string | null;
  previous_end_date: string | null;
  unit: TimeUnit;
  preset_label?: string;
}

export interface CalendarDay {
  date: dayjs.Dayjs;
  isCurrentMonth: boolean;
  isToday: boolean;
  isSelected: boolean;
  isInRange: boolean;
  isStartDate: boolean;
  isEndDate: boolean;
  isComparisonRange: boolean;
  isComparisonStart: boolean;
  isComparisonEnd: boolean;
  isDisabled: boolean;
}

export interface UnitSuggestion {
  recommended: string;
  available: string[];
}

export interface PredefinedRange {
  label: string;
  getValue: () => DateRange;
}

@Component({
  selector: 'app-normal-date-picker',
  standalone: true,
  imports: [
    CommonModule,
    IconComponent,
    IconComponentV2,
    ClickOutsideDirective,
    DropdownComponent,
  ],
  templateUrl: './normal-date-picker.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    // Note: `pointer-events` is intentionally NOT part of this animation
    // because it's a non-animatable CSS property and Angular logs a
    // warning when it's used inside `style()`. It is instead bound via
    // `[style.pointer-events]` on each element that uses `@slideInOut`
    // so the closed panel cannot capture clicks while it's invisible.
    trigger('slideInOut', [
      state(
        'closed',
        style({
          opacity: 0,
          transform: 'translateY(-8px) scale(0.95)',
        })
      ),
      state(
        'open',
        style({
          opacity: 1,
          transform: 'translateY(0) scale(1)',
        })
      ),
      transition('closed => open', [
        style({
          opacity: 0,
          transform: 'translateY(-8px) scale(0.95)',
        }),
        animate(
          '200ms cubic-bezier(0.23, 1, 0.32, 1)',
          style({
            opacity: 1,
            transform: 'translateY(0) scale(1)',
          })
        ),
      ]),
      transition('open => closed', [
        animate(
          '150ms cubic-bezier(0.4, 0.0, 0.2, 1)',
          style({
            opacity: 0,
            transform: 'translateY(-8px) scale(0.95)',
          })
        ),
      ]),
    ]),
  ],
})
export class NormalDatePickerComponent
  implements OnInit, OnDestroy, AfterViewInit, OnChanges {
  @ViewChild('containerRef', { static: true }) containerRef!: ElementRef;
  @ViewChild('startDate', { static: false }) startDate!: ElementRef;
  @ViewChild('endDate', { static: false }) endDate!: ElementRef;
  @ViewChild('triggerRef', { static: false }) triggerRef!: ElementRef;
  @ViewChild('expandedContainer', { static: false })
  expandedContainer?: ElementRef;
  @ViewChild('groupByButton', { static: false }) groupByButton?: ElementRef;
  @ViewChild('expandedComparisionContainer', { static: false })
  expandedComparisionContainer?: ElementRef;
  @ViewChild('expandedGroupContainer', { static: false })
  expandedGroupContainer?: ElementRef;
  @ViewChild('timeUnitDropdown', { static: false })
  timeUnitDropdown?: DropdownComponent;

  // Configuration Inputs
  @Input() value?: DateRangePickerValue;
  @Input() placeholder = 'Select date range';
  @Input() disabled = false;
  @Input() minDate?: string; // ISO date string
  @Input() maxDate?: string; // ISO date string
  @Input() allowComparison = true;
  @Input() showComparison = true;
  @Input() showGroupBy = true;
  @Input() defaultTimeUnit: TimeUnit = 'month';
  @Input() size: 'sm' | 'md' | 'lg' = 'md';
  @Input() variant: 'default' | 'outline' | 'primary' = 'default';
  @Input() allowedGroupByUnits: TimeUnit[] | null = null;
  @Input() alwaysAvailableUnit: TimeUnit | TimeUnit[] | null = null;

  @Input() predefinedRangesDataSets?: PredefinedRange[];
  @Input() selectedStartRange?: any;
  @Input() selectedEndRange?: any;
  // Optional hint used as a fallback when stored start/end no longer match
  // any preset's freshly-computed range (e.g. relative ranges like
  // "Last 3 Months" whose boundaries shift after midnight / across DST).
  @Input() selectedPresetLabel?: string;
  @Input() hideWeekDayUnit?: boolean = false;
  @Input() hideRightSection?: boolean = true;
  @Input() triggerClass?: string;
  // for mobile screen changes
  @Input() isMobileMode = false;
  @Input() minYear?: number;
  @Input() maxYear?: number;

  currentYear = nowInLA().year();
  currentMonth = nowInLA().month() + 1;

  private breakpointService = inject(BreakpointService);
  bottomSheetRef = inject(MatBottomSheetRef, { optional: true });

  // Responsive mobile state
  isMobile = computed(
    () => this.isMobileMode || !!this.breakpointService.isMobile()
  );

  // Events
  @Output() valueChange = new EventEmitter<DateRangePickerValue>();
  @Output() openChange = new EventEmitter<boolean>();
  @Output() dateRangeChange = new EventEmitter<DateRangePickerOutput>();

  // Internal State
  isExpanded = signal(false);
  isComparisionExpanded = signal(false);
  isGroupExpanded = signal(false);
  expandedSection = signal<'range' | 'comparison' | 'groupby' | null>(null);

  // Calendar months
  startCalendarMonth = signal(nowInLA().subtract(1, 'month'));
  endCalendarMonth = signal(nowInLA());
  comparisonCalendarMonth1 = signal(nowInLA());
  comparisonCalendarMonth2 = signal(nowInLA().add(1, 'month'));

  // Selection State

  get selectedDateRange() {
    return {
      start: this.selectedStartRange
        ? this.selectedStartRange
        : nowInLA().subtract(6, 'day').startOf('day').toISOString(),
      end: this.selectedEndRange
        ? this.selectedEndRange
        : nowInLA().endOf('day').toISOString(),
    };
  }

  selectedRange = signal<DateRange>({
    start: this.selectedDateRange.start,
    end: this.selectedDateRange.end,
  });

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['selectedStartRange'] ||
      changes['selectedEndRange'] ||
      changes['defaultTimeUnit'] ||
      changes['predefinedRangesDataSets'] ||
      changes['selectedPresetLabel']
    ) {
      const start = this.selectedDateRange.start;
      const end = this.selectedDateRange.end;
      this.selectedRange.set({ start, end });
      this.timeUnit.set(this.getUnit);
      this.setMatchingPreset(start, end);

      if (this.isMobile()) {
        const startDate = dayjs.utc(start).tz(LA_TIMEZONE);
        this.startCalendarMonth.set(startDate.startOf('month')); 
        this.mobileBrowseDate.set(startDate.startOf('month')); 
        if (
          this.isMobileMode &&
          this.selectedStartRange &&
          this.selectedEndRange
        ) {
          const startInLA = dayjs
            .utc(this.selectedStartRange)
            .tz(LA_TIMEZONE);
          const endInLA = dayjs.utc(this.selectedEndRange).tz(LA_TIMEZONE);
          this.selectedStartDate = {
            year: startInLA.year(),
            month: startInLA.month() + 1,
          };
          this.selectedEndDate = {
            year: endInLA.year(),
            month: endInLA.month() + 1,
          };
        }
      }
    }
  }

  comparisonType = signal<ComparisonType>('none');
  comparisonRange = signal<DateRange | null>(null);

  get getUnit() {
    return this.defaultTimeUnit;
  }
  timeUnit = signal<TimeUnit>(this.getUnit);

  selectedPreset = signal<DateRangePreset>('last7days');
  isSelectingEndDate = signal(false);
  hoveredDate = signal<dayjs.Dayjs | null>(null);
  isSelectingStart = signal(true);

  // Mobile-specific state
  mobileView = signal<'year' | 'month'>('month');
  isMobilePresetOpen = signal(false);
  isMobileGroupByOpen = signal(false);

  // Year/month currently being browsed in the mobile picker. Decoupled
  // from `startCalendarMonth` so the Start and End sides can each be
  // navigated independently — picking End year/month no longer forces
  // the Start side to follow along.
  mobileBrowseDate = signal<dayjs.Dayjs>(nowInLA().startOf('month'));

  // Temporary range for calendar selections (before apply)
  temporaryRange = signal<DateRange | null>(null);
  originalRange = signal<DateRange | null>(null);

  // Tracks whether the user has explicitly picked custom Start/End
  // month-year cards in the current mobile session (vs picking a preset
  // or clearing). Used to keep the emitted `preset_label` honest: when
  // the user does a custom pick, the stale preset label / hint must not
  // bleed into the output, otherwise the host's "is this the default
  // preset?" reset detection misfires and clears its saved range.
  private customRangeActive = false;

  // Dropdown States
  isStartMonthOpen = signal(false);
  isStartYearOpen = signal(false);
  isEndMonthOpen = signal(false);
  isEndYearOpen = signal(false);
  dropdownPosition = signal<{ top: number; left: number; width: number }>({
    top: 0,
    left: 0,
    width: 0,
  });

  // Listener reference for binding/unbinding
  private repositionListener = () => this.positionPanelUnderTrigger();

  // Computed Properties
  displayText = computed(() => {
    // Show temporary range if selecting, otherwise show selected range

    const range = this.temporaryRange() || this.selectedRange();
    const start = dayjs.utc(range.start).tz(LA_TIMEZONE);
    const end = dayjs.utc(range.end).tz(LA_TIMEZONE);
    const days = end.diff(start, 'day') + 1;

    return `${this.formatDate(start)} → ${this.formatDate(end)} (${days}d)`;
  });

  comparisonLabel = computed(() => {
    const type = this.comparisonType();
    if (type === 'none') return 'No Comparison';
    if (type === 'previous-period') return 'Previous Period';
    if (type === 'previous-month') return 'Previous Month';
    if (type === 'previous-year') return 'Previous Year';
    return 'Custom Range';
  });

  startDateOpen() {
    this.startDate.nativeElement.showPicker();
  }

  get predefinedRangesList() {
    return this.predefinedRangesDataSets
      ? this.predefinedRangesDataSets
      : this.predefinedRanges;
  }

  comparisonDates = computed(() => {
    const range = this.comparisonRange();
    if (!range) return '';
    const start = dayjs.utc(range.start).tz(LA_TIMEZONE);
    const end = dayjs.utc(range.end).tz(LA_TIMEZONE);
    return `${this.formatDate(start)} - ${this.formatDate(end)}`;
  });

  previousPeriodDates = computed(() => {
    const range = this.selectedRange();
    const start = dayjs.utc(range.start).tz(LA_TIMEZONE);
    const end = dayjs.utc(range.end).tz(LA_TIMEZONE);
    const days = end.diff(start, 'day');
    const comparisonEnd = start.subtract(1, 'day');
    const comparisonStart = comparisonEnd.subtract(days, 'day');

    return `${this.formatDate(comparisonStart)} - ${this.formatDate(
      comparisonEnd
    )}`;
  });

  previousMonthDates = computed(() => {
    const range = this.selectedRange();
    const start = dayjs.utc(range.start).tz(LA_TIMEZONE);
    const end = dayjs.utc(range.end).tz(LA_TIMEZONE);
    const days = end.diff(start, 'day');
    const comparisonEnd = end.subtract(1, 'month');
    const comparisonStart = start.subtract(1, 'month');

    return `${this.formatDate(comparisonStart)} - ${this.formatDate(
      comparisonEnd
    )}`;
  });

  previousYearDates = computed(() => {
    const range = this.selectedRange();
    const start = dayjs.utc(range.start).tz(LA_TIMEZONE);
    const end = dayjs.utc(range.end).tz(LA_TIMEZONE);
    const days = end.diff(start, 'day');
    const comparisonEnd = end.subtract(1, 'year');
    const comparisonStart = start.subtract(1, 'year');

    return `${this.formatDate(comparisonStart)} - ${this.formatDate(
      comparisonEnd
    )}`;
  });

  currentMonthDays = computed(() => {
    // Use temporary range for display if available, otherwise use selected range
    const displayRange = this.temporaryRange() || this.selectedRange();
    return this.generateCalendarDays(this.startCalendarMonth(), displayRange);
  });

  nextMonthDays = computed(() => {
    // Use temporary range for display if available, otherwise use selected range
    const displayRange = this.temporaryRange() || this.selectedRange();
    return this.generateCalendarDays(this.endCalendarMonth(), displayRange);
  });

  comparisonMonth1Days = computed(() => {
    const range = this.comparisonRange();
    if (!range) return [];
    return this.generateCalendarDays(this.comparisonCalendarMonth1(), range);
  });

  comparisonMonth2Days = computed(() => {
    const range = this.comparisonRange();
    if (!range) return [];
    return this.generateCalendarDays(this.comparisonCalendarMonth2(), range);
  });

  groupByWidth = computed(() => {
    // Static width for group by dropdown
    return 240;
  });

  triggerPadding = computed(() => {
    switch (this.size) {
      case 'sm':
        return 'py-1.5';
      case 'md':
        return 'py-2';
      case 'lg':
        return 'py-3';
      default:
        return 'py-2';
    }
  });

  // Smart time unit calculation based on date range
  availableTimeUnits = computed(() => {
    const range = this.selectedRange();
    const startTime = dayjs.utc(range.start).valueOf();
    const endTime = dayjs.utc(range.end).valueOf();

    if (isNaN(startTime) || isNaN(endTime)) {
      let base = [
        {
          label: 'Day',
          value: 'day' as TimeUnit,
          icon: 'calendar' as IconName,
        },
        {
          label: 'Week',
          value: 'week' as TimeUnit,
          icon: 'calendar' as IconName,
        },
      ];
      base = this.filterUnits(base);
      return base;
    }

    const diffMs = Math.abs(endTime - startTime);
    const oneDay = 86400000;
    const sevenDays = oneDay * 7;
    const oneMonth = 2629800000;
    const threeMonths = oneMonth * 3;
    const sixMonths = oneMonth * 6;
    const oneYear = oneDay * 365;

    let available: TimeUnit[] = [];

    if (diffMs <= oneDay) {
      available = ['day', 'hour'];
    } else if (diffMs <= sevenDays) {
      available = ['day', 'hour'];
    } else if (diffMs <= oneMonth) {
      available = ['day', 'week', 'month'];
    } else if (diffMs <= threeMonths) {
      available = ['week', 'month'];
    } else if (diffMs <= sixMonths) {
      available = ['week', 'month'];
    } else if (diffMs <= oneYear) {
      available = ['month', 'quarter'];
    } else {
      available = ['quarter', 'year'];
    }

    // Map available units to full objects with labels and icons
    const unitMap: Record<TimeUnit, { label: string; icon: IconName }> = {
      hour: { label: 'Hour', icon: 'calendar' },
      day: { label: 'Day', icon: 'calendar' },
      week: { label: 'Week', icon: 'calendar' },
      month: { label: 'Month', icon: 'calendar' },
      quarter: { label: 'Quarter', icon: 'chartNoAxesCombined' },
      year: { label: 'Year', icon: 'chartNoAxesCombined' },
    };

    const base = available.map((unit) => ({
      label: unitMap[unit].label,
      value: unit,
      icon: unitMap[unit].icon,
    }));
    return this.filterUnits(base);
  });

  // Static Data
  weekDays = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
  monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  predefinedRanges: PredefinedRange[] = [
    {
      label: 'Today',
      getValue: () => {
        const today = nowInLA();
        return {
          start: today.startOf('day').toISOString(),
          end: today.endOf('day').toISOString(),
        };
      },
    },
    {
      label: 'Yesterday',
      getValue: () => {
        const yesterday = nowInLA().subtract(1, 'day');
        return {
          start: yesterday.startOf('day').toISOString(),
          end: yesterday.endOf('day').toISOString(),
        };
      },
    },
    {
      label: 'Last 7 Days',
      getValue: () => ({
        start: nowInLA().subtract(6, 'day').startOf('day').toISOString(),
        end: nowInLA().endOf('day').toISOString(),
      }),
    },
    {
      label: 'Last 14 Days',
      getValue: () => ({
        start: nowInLA().subtract(13, 'day').startOf('day').toISOString(),
        end: nowInLA().endOf('day').toISOString(),
      }),
    },
    {
      label: 'Last 30 Days',
      getValue: () => ({
        start: nowInLA().subtract(29, 'day').startOf('day').toISOString(),
        end: nowInLA().endOf('day').toISOString(),
      }),
    },
    {
      label: 'Last 90 Days',
      getValue: () => ({
        start: nowInLA().subtract(89, 'day').startOf('day').toISOString(),
        end: nowInLA().endOf('day').toISOString(),
      }),
    },
    {
      label: 'This Week',
      getValue: () => {
        const now = nowInLA();
        const startOfWeek = now.startOf('week');
        return {
          start: startOfWeek.toISOString(),
          end: now.endOf('day').toISOString(),
        };
      },
    },
    {
      label: 'Last Week',
      getValue: () => {
        const now = nowInLA();
        const startOfLastWeek = now.subtract(1, 'week').startOf('week');
        const endOfLastWeek = now.subtract(1, 'week').endOf('week');
        return {
          start: startOfLastWeek.toISOString(),
          end: endOfLastWeek.toISOString(),
        };
      },
    },
    {
      label: 'This Month',
      getValue: () => {
        const now = nowInLA();
        return {
          start: now.startOf('month').toISOString(),
          end: now.endOf('day').toISOString(),
        };
      },
    },
    {
      label: 'Last Month',
      getValue: () => {
        const lastMonth = nowInLA().subtract(1, 'month');
        return {
          start: lastMonth.startOf('month').toISOString(),
          end: lastMonth.endOf('month').toISOString(),
        };
      },
    },
    {
      label: 'This Quarter',
      getValue: () => {
        const now = nowInLA();
        const quarter = Math.floor(now.month() / 3);
        const quarterStart = now.month(quarter * 3).startOf('month');
        return {
          start: quarterStart.toISOString(),
          end: now.endOf('day').toISOString(),
        };
      },
    },
    {
      label: 'Last Quarter',
      getValue: () => {
        const now = nowInLA().subtract(3, 'month');
        const quarter = Math.floor(now.month() / 3);
        const quarterStart = now.month(quarter * 3).startOf('month');
        const quarterEnd = now.month(quarter * 3 + 2).endOf('month');
        return {
          start: quarterStart.toISOString(),
          end: quarterEnd.toISOString(),
        };
      },
    },
    {
      label: 'This Year',
      getValue: () => {
        const now = nowInLA();
        return {
          start: now.startOf('year').toISOString(),
          end: now.endOf('day').toISOString(),
        };
      },
    },
    {
      label: 'Last Year',
      getValue: () => {
        const lastYear = nowInLA().subtract(1, 'year');
        return {
          start: lastYear.startOf('year').toISOString(),
          end: lastYear.endOf('year').toISOString(),
        };
      },
    },
    {
      label: 'Last 2 years',
      getValue: () => {
        const last2Years = nowInLA().subtract(2, 'year');
        return {
          start: last2Years.startOf('year').toISOString(),
          end: nowInLA().endOf('day').toISOString(),
        };
      },
    },
    {
      label: 'Last 3 years',
      getValue: () => {
        const last3Years = nowInLA().subtract(3, 'year');
        return {
          start: last3Years.startOf('year').toISOString(),
          end: nowInLA().endOf('day').toISOString(),
        };
      },
    },
    {
      label: 'Last 5 years',
      getValue: () => {
        const last5Years = nowInLA().subtract(5, 'year');
        return {
          start: last5Years.startOf('year').toISOString(),
          end: nowInLA().endOf('day').toISOString(),
        };
      },
    },
    {
      label: 'All Time',
      getValue: () => ({
        start: '2017-01-01T00:00:00.000Z',
        end: nowInLA().endOf('day').toISOString(),
      }),
    },
  ];

  timeUnits: Array<{ value: TimeUnit; label: string; icon: IconName }> = [
    { value: 'hour', label: 'Hour', icon: 'calendar' },
    { value: 'day', label: 'Day', icon: 'calendar' },
    { value: 'week', label: 'Week', icon: 'calendar' },
    { value: 'month', label: 'Month', icon: 'calendar' },
    { value: 'quarter', label: 'Quarter', icon: 'chartNoAxesCombined' },
    { value: 'year', label: 'Year', icon: 'chartNoAxesCombined' },
  ];

  timeUnitOptions: DropdownOption[] = this.timeUnits.map((unit) => ({
    value: unit.value,
    label: unit.label,
    icon: unit.icon,
  }));

  public availableUnitIndexes: any[] = this.timeUnits.map((_, i) => i);

  ngOnInit(): void {
    this.initializeFromValue();
    // Emit initial date range on component init
    this.emitDateRangeChange();
  }

  ngAfterViewInit(): void {
    // Set initial time unit value in dropdown
    if (this.timeUnitDropdown) {
      this.timeUnitDropdown.writeValue(this.timeUnit());
    }
  }

  // Portal container when picker is opened to escape parent stacking contexts
  private portalRoot: HTMLElement | null = null;

  private attachToBody(): void {
    try {
      if (!this.portalRoot) {
        this.portalRoot = document.createElement('div');
        this.portalRoot.style.position = 'absolute';
        this.portalRoot.style.top = '0';
        this.portalRoot.style.left = '0';
        this.portalRoot.style.width = '100%';
        this.portalRoot.style.pointerEvents = 'none';
        document.body.appendChild(this.portalRoot);
      }

      if (
        this.expandedContainer &&
        this.expandedContainer.nativeElement &&
        this.portalRoot
      ) {
        // Move the expanded container element into the portal root
        this.portalRoot.appendChild(this.expandedContainer.nativeElement);
        this.expandedContainer.nativeElement.style.pointerEvents = 'auto';
      }
    } catch (e) {
      console.error(e);
    }
  }

  // UPDATED: Fixed positioning logic
  private positionPanelUnderTrigger(): void {
    try {
      const trigger = this.triggerRef?.nativeElement as HTMLElement;
      const panel = this.expandedContainer?.nativeElement as HTMLElement;
      if (!trigger || !panel) return;

      const rect = trigger.getBoundingClientRect();
      const panelRect = panel.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const gap = 8;
      const margin = 8;

      let panelWidth = panelRect.width || panel.offsetWidth || 360;
      if (!panelWidth || panelWidth < 10) {
        const styleWidth = panel.style.width;
        if (styleWidth && styleWidth.endsWith('px')) {
          panelWidth = parseFloat(styleWidth.replace('px', '')) || 360;
        } else {
          panelWidth = 360;
        }
      }

      // Left Positioning (Fixed uses Viewport coordinates)
      let left = rect.left;

      if (left + panelWidth > viewportWidth - margin) {
        left = Math.max(margin, rect.right - panelWidth);
      }
      if (left < margin) {
        left = margin;
      }

      // Top Positioning
      const panelHeight = panelRect.height || panel.offsetHeight || 400;
      
      // Default: Place below trigger
      let top = rect.bottom + gap;

      // Check overflow bottom
      if (top + panelHeight > viewportHeight - margin) {
        const altTop = rect.top - gap - panelHeight;
        if (altTop >= margin) {
          top = altTop;
        } else {
          top = viewportHeight - margin - panelHeight;
        }
      }

      // Use FIXED positioning to avoid scrolling issues
      panel.style.position = 'fixed';
      panel.style.top = `${top}px`;
      panel.style.left = `${left}px`;
      panel.style.transform = '';
      panel.style.pointerEvents = 'auto';
      panel.style.zIndex = '3000';
    } catch (e) {
      // ignore
    }
  }

  // UPDATED: Scroll listener logic with Capture
  private addScrollListeners(): void {
    // Capture: true ensures we catch scroll events from any div within the document
    document.addEventListener('scroll', this.repositionListener, true);
    window.addEventListener('resize', this.repositionListener);
  }

  private removeScrollListeners(): void {
    document.removeEventListener('scroll', this.repositionListener, true);
    window.removeEventListener('resize', this.repositionListener);
  }

  ngOnDestroy(): void {
    this.removeScrollListeners();
    if (this.portalRoot && document.body.contains(this.portalRoot)) {
      document.body.removeChild(this.portalRoot);
    }
  }

  // Event Handlers
  toggle(): void {
    if (this.disabled) return;
    this.isComparisionExpanded.set(false);
    this.isGroupExpanded.set(false);

    const wasExpanded = this.isExpanded();
    this.isExpanded.set(!wasExpanded);

    if (this.isExpanded()) {
      setTimeout(() => {
        this.calculatePosition();
        this.attachToBody();
        this.positionPanelUnderTrigger();
        this.addScrollListeners(); // Add listeners
      }, 0);
      this.closeAllDropdowns();
    } else {
      this.removeScrollListeners(); // Remove listeners
    }

    this.openChange.emit(this.isExpanded());
  }

  toggleComparision(): void {
    if (this.disabled) return;
    this.isExpanded.set(false);
    this.isGroupExpanded.set(false);

    const wasExpanded = this.isComparisionExpanded();
    this.isComparisionExpanded.set(!wasExpanded);

    if (this.isComparisionExpanded()) {
      setTimeout(() => {
        this.calculatePosition();
        this.addScrollListeners(); // Add listeners
      }, 0);
      this.closeAllDropdowns();
    } else {
      this.removeScrollListeners();
    }

    this.openChange.emit(this.isComparisionExpanded());
  }

  toggleGroup(): void {
    if (this.disabled) return;
    this.isExpanded.set(false);
    this.isComparisionExpanded.set(false);

    const wasExpanded = this.isGroupExpanded();
    this.isGroupExpanded.set(!wasExpanded);

    if (this.isGroupExpanded()) {
      setTimeout(() => {
        this.calculatePosition();
        this.addScrollListeners(); // Add listeners
      }, 0);
      this.closeAllDropdowns();
    } else {
      this.removeScrollListeners();
    }

    this.openChange.emit(this.isGroupExpanded());
  }

  close(): void {
    if (this.isMobileMode && this.bottomSheetRef) {
      this.bottomSheetRef.dismiss();
      return;
    }

    const originalRange = this.originalRange();
    if (originalRange && this.temporaryRange()) {
      this.selectedRange.set(originalRange);
    }

    this.temporaryRange.set(null);
    this.originalRange.set(null);
    this.isSelectingStart.set(true);

    this.removeScrollListeners(); // Remove listeners

    this.isExpanded.set(false);
    this.isGroupExpanded.set(false);
    this.isComparisionExpanded.set(false);
    this.expandedSection.set(null);
    this.closeAllDropdowns();
    this.openChange.emit(false);
  }

  calculatePosition(): void {
    this.dropdownPosition.set({ top: 0, left: 0, width: 864 });
  }

  closeAllDropdowns(): void {
    this.isStartMonthOpen.set(false);
    this.isStartYearOpen.set(false);
    this.isEndMonthOpen.set(false);
    this.isEndYearOpen.set(false);
  }

  onClickOutside(): void {
    if (this.isMobileMode && this.bottomSheetRef) {
      return;
    }
    this.close();
  }

  toggleStartMonthDropdown(): void {
    const wasOpen = this.isStartMonthOpen();
    this.closeAllDropdowns();
    this.isStartMonthOpen.set(!wasOpen);
  }

  toggleStartYearDropdown(): void {
    const wasOpen = this.isStartYearOpen();
    this.closeAllDropdowns();
    this.isStartYearOpen.set(!wasOpen);
  }

  toggleEndMonthDropdown(): void {
    const wasOpen = this.isEndMonthOpen();
    this.closeAllDropdowns();
    this.isEndMonthOpen.set(!wasOpen);
  }

  toggleEndYearDropdown(): void {
    const wasOpen = this.isEndYearOpen();
    this.closeAllDropdowns();
    this.isEndYearOpen.set(!wasOpen);
  }

  setStartMonthAndClose(month: number): void {
    const newDate = this.startCalendarMonth().month(month);
    this.startCalendarMonth.set(newDate);
    this.endCalendarMonth.set(newDate.add(1, 'month'));
    this.isStartMonthOpen.set(false);
  }

  setStartYearAndClose(year: number): void {
    const newDate = this.startCalendarMonth().year(year);
    this.startCalendarMonth.set(newDate);
    this.endCalendarMonth.set(newDate.add(1, 'month'));
    this.isStartYearOpen.set(false);
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    if (
      this.isExpanded() ||
      this.isComparisionExpanded() ||
      this.isGroupExpanded()
    ) {
      this.calculatePosition();
      this.positionPanelUnderTrigger();
    }
  }

  @HostListener('window:scroll')
  onWindowScroll(): void {
    if (
      this.isExpanded() ||
      this.isComparisionExpanded() ||
      this.isGroupExpanded()
    ) {
      this.calculatePosition();
      this.positionPanelUnderTrigger();
    }
  }

  toggleSection(section: 'range' | 'comparison' | 'groupby'): void {
    const current = this.expandedSection();
    this.expandedSection.set(section);
  }

  selectedType = signal<string>('Last 7 Days');

  selectPreset(preset: PredefinedRange): void {
    this.selectedStartDate = null;
    this.selectedEndDate = null;

    this.selectedType.set(preset.label);
    const range = preset.getValue();
    this.selectedRange.set(range);
    this.selectedPreset.set('custom');
    this.updateCalendarMonths(dayjs.tz(range.start, LA_TIMEZONE));
    this.autoSelectTimeUnit();
    this.updateComparison();
    this.close();
    this.emitValue();
    this.emitDateRangeChange();
  }

  private autoSelectTimeUnit(): void {
    const availableUnits = this.availableTimeUnits().filter(
      (unit) =>
        unit.value !== 'hour' && unit.value !== 'day' && unit.value !== 'week'
    );
    if (availableUnits.length > 0) {
      const firstUnit = availableUnits[0].value;
      if (firstUnit === 'hour' && availableUnits.length > 1) {
        this.timeUnit.set(availableUnits[1].value);
      } else {
        this.timeUnit.set(firstUnit);
      }
    }
  }

  dateRange = signal<{ startDate: string; endDate: string; unit: string }>({
    startDate: '',
    endDate: '',
    unit: '',
  });

  calculateDateRange(unit: TimeUnit): void {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    let start: Date;
    let end: Date;

    switch (unit) {
      case 'hour':
        start = new Date(today);
        start.setHours(today.getHours() - 1);
        end = new Date(today);
        break;

      case 'day':
        start = new Date(today);
        end = new Date(today);
        break;

      case 'week':
        const dayOfWeek = today.getDay();
        start = new Date(today);
        start.setDate(today.getDate() - dayOfWeek);
        end = new Date(today);
        break;

      case 'month':
        start = new Date(today.getFullYear(), today.getMonth(), 1);
        end = new Date(today);
        break;

      case 'quarter':
        const currentMonth = today.getMonth();
        const startMonth = currentMonth - (currentMonth % 3);
        start = new Date(today.getFullYear(), startMonth, 1);
        end = new Date(today);
        break;

      case 'year':
        start = new Date(today.getFullYear(), 0, 1);
        end = new Date(today);
        break;

      default:
        start = new Date(today);
        end = new Date(today);
        break;
    }

    this.dateRange.set({
      startDate: dayjs(start).format('MM-DD-YYYY'),
      endDate: dayjs(end).format('MM-DD-YYYY'),
      unit: unit,
    });
  }

  onTimeUnitChange(unit: string, index: number): void {
    this.isUnitManuallySelected.set(true);
    this.timeUnit.set(unit as TimeUnit);
    this.selectedUnitIndex = index;
    this.updateComparison();
    this.calculateDateRange(unit as TimeUnit);
    this.isGroupExpanded.set(false);
    this.emitValue();
    this.emitDateRangeChange();
  }

  setComparisonType(type: ComparisonType): void {
    this.comparisonType.set(type);
    if (type === 'none') {
      this.comparisonRange.set(null);
    } else if (type === 'custom') {
      if (!this.comparisonRange()) {
        const now = dayjs.utc();
        this.comparisonRange.set({
          start: now.subtract(1, 'month').toISOString(),
          end: now.subtract(1, 'week').toISOString(),
        });
      }
    } else {
      this.updateComparison();
    }
    this.isComparisionExpanded.set(false);
    this.emitValue();
    this.emitDateRangeChange();
  }

  selectedDateRangeValue = signal<{ start: string; end: string } | null>(null);
  selectedStartDate: { year: number; month: number } | null = null;
  selectedEndDate: { year: number; month: number } | null = null;

  onDateRangeFilter(
    event: { year: number; month: number } | null,
    type: 'start' | 'end'
  ) {
    if (!event) return;

    if (type === 'start') {
      this.selectedStartDate = {
        year: event.year,
        month: event.month,
      };

      if (
        this.selectedEndDate &&
        (this.selectedEndDate.year < event.year ||
          (this.selectedEndDate.year === event.year &&
            this.selectedEndDate.month < event.month))
      ) {
        this.selectedEndDate = null;
      }
    }

    if (type === 'end') {
      this.selectedEndDate = {
        year: event.year,
        month: event.month,
      };
    }
  }

  applyRange(): void {
    const tempRange = this.temporaryRange();
    if (tempRange) {
      this.selectedRange.set(tempRange);
      this.autoSelectTimeUnit();
      this.updateComparison();
      this.setMatchingPreset(tempRange.start, tempRange.end);
    }

    this.temporaryRange.set(null);
    this.originalRange.set(null);
    this.isSelectingStart.set(true);

    this.isExpanded.set(false);
    this.emitValue();
    this.handleMainDateClick();

    if (this.bottomSheetRef) {
      this.bottomSheetRef.dismiss(this.buildDateRangeOutput());
      return;
    }

    this.emitDateRangeChange();
  }

  applyComparison(): void {
    this.updateComparison();
    this.isComparisionExpanded.set(false);
    this.emitValue();
    this.emitDateRangeChange();
  }

  previousMonth(): void {
    this.startCalendarMonth.set(this.startCalendarMonth().subtract(1, 'month'));
    this.endCalendarMonth.set(this.endCalendarMonth().subtract(1, 'month'));
  }

  nextMonth(): void {
    this.startCalendarMonth.set(this.startCalendarMonth().add(1, 'month'));
    this.endCalendarMonth.set(this.endCalendarMonth().add(1, 'month'));
  }

  setCalendarMonth(month: number, year: number, isEnd = false): void {
    const newDate = nowInLA().year(year).month(month).startOf('month');
    if (isEnd) {
      this.endCalendarMonth.set(newDate);
    } else {
      this.startCalendarMonth.set(newDate);
    }
  }

  setComparisonCalendarMonth(
    month: number,
    year: number,
    isSecond = false
  ): void {
    const newDate = nowInLA().year(year).month(month).startOf('month');
    if (isSecond) {
      this.comparisonCalendarMonth2.set(newDate);
    } else {
      this.comparisonCalendarMonth1.set(newDate);
    }
  }

  setComparisonRange(range: DateRange): void {
    this.comparisonRange.set(range);
  }

  onDayClick(date: dayjs.Dayjs, isComparison = false): void {
    if (this.isDateDisabled(date)) return;
    if (isComparison) {
      this.handleComparisonDateClick(date);
    } else {
      this.handleMainDateClick(date);
    }
  }

  onDateHover(date: dayjs.Dayjs): void {
    if (!this.isSelectingStart()) {
      this.hoveredDate.set(date);
    }
  }

  clearHover(): void {
    this.hoveredDate.set(null);
  }

  @HostListener('document:keydown.escape', ['$event'])
  onEscapeKey(event: any): void {
    if (
      this.isStartMonthOpen() ||
      this.isStartYearOpen() ||
      this.isEndMonthOpen() ||
      this.isEndYearOpen()
    ) {
      this.closeAllDropdowns();
      event.preventDefault();
      return;
    }
    if (
      this.isExpanded() ||
      this.isComparisionExpanded() ||
      this.isGroupExpanded()
    ) {
      this.close();
      event.preventDefault();
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (
      !this.isExpanded() &&
      !this.isComparisionExpanded() &&
      !this.isGroupExpanded()
    ) {
      return;
    }

    const target = event.target as Element;
    if (!target) return;

    const clickedInsideTrigger =
      this.triggerRef?.nativeElement?.contains(target);
    const clickedInsideExpanded =
      this.expandedContainer?.nativeElement?.contains(target);
    const clickedInsideComparison =
      this.expandedComparisionContainer?.nativeElement?.contains(target);
    const clickedInsideGroup =
      this.expandedGroupContainer?.nativeElement?.contains(target);

    if (
      !clickedInsideTrigger &&
      !clickedInsideExpanded &&
      !clickedInsideComparison &&
      !clickedInsideGroup
    ) {
      this.close();
    }
  }

  private initializeFromValue(): void {
    if (this.value) {
      this.selectedRange.set({
        start: this.value.startDate,
        end: this.value.endDate,
      });
      this.timeUnit.set(this.value.unit);
      this.comparisonType.set(this.value.comparisonType);

      if (this.value.comparisonStartDate && this.value.comparisonEndDate) {
        this.comparisonRange.set({
          start: this.value.comparisonStartDate,
          end: this.value.comparisonEndDate,
        });
      }

      const startDate = dayjs.tz(this.value.startDate, LA_TIMEZONE);
      const endDate = dayjs.tz(this.value.endDate, LA_TIMEZONE);
      // this.selectedStartDate = {
      //   year: startDate.year(),
      //   month: startDate.month() + 1,
      // };
      // this.selectedEndDate = {
      //   year: endDate.year(),
      //   month: endDate.month() + 1,
      // };
      this.updateCalendarMonths(startDate, endDate);
      this.setMatchingPreset(this.value.startDate, this.value.endDate);
    } else {
      const defaultRange = this.selectedRange();
      // if (defaultRange && defaultRange.start && defaultRange.end) {
      //   const startDate = dayjs.tz(defaultRange.start, LA_TIMEZONE);
      //   const endDate = dayjs.tz(defaultRange.end, LA_TIMEZONE);
      //   this.selectedStartDate = {
      //     year: startDate.year(),
      //     month: startDate.month() + 1,
      //   };
      //   this.selectedEndDate = {
      //     year: endDate.year(),
      //     month: endDate.month() + 1,
      //   };
      // }
      this.setMatchingPreset(defaultRange.start, defaultRange.end);
    }
  }

  private setMatchingPreset(start: string, end: string): void {
    const matchingPreset = this.predefinedRangesList.find((preset) => {
      const range = preset.getValue();
      return (
        dayjs.utc(range.start).isSame(dayjs.utc(start), 'day') &&
        dayjs.utc(range.end).isSame(dayjs.utc(end), 'day')
      );
    });

    if (matchingPreset) {
      this.selectedType.set(matchingPreset.label);
      return;
    }

    // Fallback: relative presets (e.g. "Last 3 Months") drift after midnight
    // or across DST, so a stored snapshot may no longer match any preset's
    // freshly-computed range. If the caller supplied the originally-selected
    // label and it still exists in the preset list, retain it so the trigger
    // doesn't silently revert to "Tap to Select" on reopen.
    const hintedLabel = this.selectedPresetLabel;
    if (
      hintedLabel &&
      this.predefinedRangesList.some((p) => p.label === hintedLabel)
    ) {
      this.selectedType.set(hintedLabel);
      return;
    }

    this.selectedType.set('');
  }

  private updateCalendarMonths(
    startDate: dayjs.Dayjs,
    endDate?: dayjs.Dayjs
  ): void {
    if (endDate) {
      this.startCalendarMonth.set(startDate.startOf('month'));
      this.endCalendarMonth.set(endDate.startOf('month'));
      return;
    }
    this.startCalendarMonth.set(
      startDate.subtract(1, 'month').startOf('month')
    );
    this.endCalendarMonth.set(startDate.startOf('month'));
  }

  private handleMainDateClick(date?: dayjs.Dayjs): void {
    if (this.selectedStartDate && this.selectedEndDate) {
      const start = dayjs
        .tz(
          `${this.selectedStartDate.year}-${String(
            this.selectedStartDate.month
          ).padStart(2, '0')}-01`,
          LA_TIMEZONE
        )
        .startOf('month')
        .utc();

      const end = dayjs
        .tz(
          `${this.selectedEndDate.year}-${String(
            this.selectedEndDate.month
          ).padStart(2, '0')}-01`,
          LA_TIMEZONE
        )
        .endOf('month')
        .utc();

      const tempRange = {
        start: start.toISOString(),
        end: end.toISOString(),
      };

      this.temporaryRange.set(tempRange);
      this.selectedRange.set(tempRange);
    }
  }

  private handleComparisonDateClick(date: dayjs.Dayjs): void {
    const currentRange = this.comparisonRange();
    if (!currentRange) {
      const normalizedDate = date.startOf('day');
      this.comparisonRange.set({
        start: normalizedDate.utc().toISOString(),
        end: normalizedDate.utc().toISOString(),
      });
      return;
    }

    const start = dayjs.utc(currentRange.start).tz(LA_TIMEZONE);
    const normalizedDate = date.startOf('day');

    if (normalizedDate.isSameOrAfter(start, 'day')) {
      this.comparisonRange.set({
        ...currentRange,
        end: normalizedDate.utc().toISOString(),
      });
    } else {
      this.comparisonRange.set({
        start: normalizedDate.utc().toISOString(),
        end: currentRange.start,
      });
    }
  }

  private generateCalendarDays(
    month: dayjs.Dayjs,
    range: DateRange
  ): CalendarDay[] {
    const days: CalendarDay[] = [];
    const startOfMonth = month.startOf('month');
    const endOfMonth = month.endOf('month');
    const startOfCalendar = startOfMonth.startOf('week').startOf('day');
    const endOfCalendar = endOfMonth.endOf('week').endOf('day');

    let current = startOfCalendar;
    const rangeStart = dayjs.utc(range.start).tz(LA_TIMEZONE).startOf('day');
    const rangeEnd = dayjs.utc(range.end).tz(LA_TIMEZONE).startOf('day');

    while (current.isSameOrBefore(endOfCalendar)) {
      days.push(this.createCalendarDay(current, month, rangeStart, rangeEnd));
      current = current.add(1, 'day');
    }
    return days;
  }

  private createCalendarDay(
    date: dayjs.Dayjs,
    month: dayjs.Dayjs,
    rangeStart: dayjs.Dayjs,
    rangeEnd: dayjs.Dayjs
  ): CalendarDay {
    const compRange = this.comparisonRange();
    const compStart = compRange
      ? dayjs.utc(compRange.start).tz(LA_TIMEZONE).startOf('day')
      : null;
    const compEnd = compRange
      ? dayjs.utc(compRange.end).tz(LA_TIMEZONE).startOf('day')
      : null;
    const today = nowInLA().startOf('day');
    const hovered = this.hoveredDate();

    const normalizedDate = date.startOf('day');
    let previewStart = rangeStart;
    let previewEnd = rangeEnd;

    if (!this.isSelectingStart() && hovered) {
      const normalizedHovered = hovered.startOf('day');
      if (normalizedHovered.isSameOrAfter(rangeStart, 'day')) {
        previewEnd = normalizedHovered;
      } else {
        previewStart = normalizedHovered;
        previewEnd = rangeStart;
      }
    }

    return {
      date,
      isCurrentMonth: normalizedDate.isSame(month, 'month'),
      isToday: normalizedDate.isSame(today, 'day'),
      isSelected:
        normalizedDate.isSame(previewStart, 'day') ||
        normalizedDate.isSame(previewEnd, 'day'),
      isInRange:
        normalizedDate.isAfter(previewStart, 'day') &&
        normalizedDate.isBefore(previewEnd, 'day'),
      isStartDate: normalizedDate.isSame(previewStart, 'day'),
      isEndDate: normalizedDate.isSame(previewEnd, 'day'),
      isComparisonRange: !!(
        compStart &&
        compEnd &&
        normalizedDate.isAfter(compStart, 'day') &&
        normalizedDate.isBefore(compEnd, 'day')
      ),
      isComparisonStart: !!(
        compStart && normalizedDate.isSame(compStart, 'day')
      ),
      isComparisonEnd: !!(compEnd && normalizedDate.isSame(compEnd, 'day')),
      isDisabled: this.isDateDisabled(date),
    };
  }

  private isDateDisabled(date: dayjs.Dayjs): boolean {
    const today = nowInLA().startOf('day');
    if (date.isAfter(today, 'day')) {
      return true;
    }
    if (this.minDate && date.isBefore(dayjs.utc(this.minDate), 'day')) {
      return true;
    }
    if (this.maxDate && date.isAfter(dayjs.utc(this.maxDate), 'day')) {
      return true;
    }
    return false;
  }

  private updateComparison(): void {
    let type = this.comparisonType();
    if (type === 'none') {
      this.comparisonRange.set(null);
      return;
    }

    const range = this.selectedRange();
    const start = dayjs.utc(range.start);
    const end = dayjs.utc(range.end);

    if (
      dayjs(end).diff(dayjs(start), 'day') > 31 &&
      type === 'previous-month'
    ) {
      type = 'previous-period';
      this.setComparisonType(type);
    }

    if (type === 'previous-period') {
      const days = end.diff(start, 'day');
      const comparisonEnd = start.subtract(1, 'day');
      const comparisonStart = comparisonEnd.subtract(days, 'day');
      this.comparisonRange.set({
        start: comparisonStart.toISOString(),
        end: comparisonEnd.toISOString(),
      });
    } else if (type === 'previous-month') {
      this.comparisonRange.set({
        start: start.subtract(1, 'month').toISOString(),
        end: end.subtract(1, 'month').toISOString(),
      });
    } else if (type === 'previous-year') {
      this.comparisonRange.set({
        start: start.subtract(1, 'year').toISOString(),
        end: end.subtract(1, 'year').toISOString(),
      });
    }
  }

  private isUnitManuallySelected = signal(false);
  private getMonthDiff(
    start: { year: number; month: number },
    end: { year: number; month: number }
  ): number {
    return (end.year - start.year) * 12 + (end.month - start.month);
  }
  private emitDateRangeChange(): void {
    const range = this.selectedRange();
    const compRange = this.comparisonRange();
    const unit = this.timeUnit();

    let previousStartDate: string | null = null;
    let previousEndDate: string | null = null;

    if (this.comparisonType() !== 'none') {
      if (compRange) {
        previousStartDate = compRange.start;
        previousEndDate = compRange.end;
      } else if (this.comparisonType() === 'previous-period') {
        const start = dayjs.utc(range.start);
        const end = dayjs.utc(range.end);
        const days = end.diff(start, 'day');
        const comparisonEnd = start.subtract(1, 'day');
        const comparisonStart = comparisonEnd.subtract(days, 'day');
        previousStartDate = comparisonStart.toISOString();
        previousEndDate = comparisonEnd.toISOString();
      } else if (this.comparisonType() === 'previous-year') {
        const start = dayjs.utc(range.start);
        const end = dayjs.utc(range.end);
        previousStartDate = start.subtract(1, 'year').toISOString();
        previousEndDate = end.subtract(1, 'year').toISOString();
      }
    }

    let units = this.availableTimeUnits().filter(
      (unit) =>
        unit.value !== 'hour' && unit.value !== 'day' && unit.value !== 'week'
    );

    if (
      this.selectedStartDate &&
      this.selectedEndDate &&
      this.getMonthDiff(this.selectedStartDate, this.selectedEndDate) > 1
    ) {
      units = units.filter((unit) => unit.value !== 'month');
    }

    const resolvedUnit = this.isUnitManuallySelected()
      ? this.timeUnit()
      : units[0]?.value;

    const output: DateRangePickerOutput = {
      start_date: range.start,
      end_date: range.end,
      previous_start_date: previousStartDate,
      previous_end_date: previousEndDate,
      unit: resolvedUnit,
    };

    this.timeUnit.set(resolvedUnit);

    let obj: any = { start_date: '', end_date: '', unit: '' };

    if (this.selectedStartDate && this.selectedEndDate) {
      obj.start_date = `${this.selectedStartDate.year}-${String(
        this.selectedStartDate.month
      ).padStart(2, '0')}`;
      obj.end_date = `${this.selectedEndDate.year}-${String(
        this.selectedEndDate.month
      ).padStart(2, '0')}`;
      obj.unit = resolvedUnit;

      const start = dayjs
        .tz(
          `${this.selectedStartDate.year}-${String(
            this.selectedStartDate.month
          ).padStart(2, '0')}-01`,
          LA_TIMEZONE
        )
        .startOf('month')
        .utc();

      const end = dayjs
        .tz(
          `${this.selectedEndDate.year}-${String(
            this.selectedEndDate.month
          ).padStart(2, '0')}-01`,
          LA_TIMEZONE
        )
        .endOf('month')
        .utc();

      this.selectedRange.set({
        start: start.toISOString(),
        end: end.toISOString(),
      });

      this.dateRangeChange.emit(obj);
      this.isUnitManuallySelected.set(false);
    } else {
      this.dateRangeChange.emit(output);
      this.isUnitManuallySelected.set(false);
    }
  }

  toUtc(dt: string | number, dateInstance: boolean = false) {
    const date = new Date(dt);
    if (dateInstance) {
      return new Date(
        date.getUTCFullYear(),
        date.getUTCMonth(),
        date.getUTCDate(),
        date.getUTCHours(),
        date.getUTCMinutes(),
        date.getUTCSeconds(),
        date.getUTCMilliseconds()
      );
    } else {
      return Date.UTC(
        date.getUTCFullYear(),
        date.getUTCMonth(),
        date.getUTCDate(),
        date.getUTCHours(),
        date.getUTCMinutes(),
        date.getUTCSeconds(),
        date.getUTCMilliseconds()
      );
    }
  }

  private getUnitSuggestions(
    startDate: string,
    endDate: string
  ): UnitSuggestion {
    const TIME_UNITS_CONFIG = [
      { unit: 'year', durationMs: 31557600000 },
      { unit: 'quarter', durationMs: 7889400000 },
      { unit: 'month', durationMs: 2629800000 },
      { unit: 'week', durationMs: 604800000 },
      { unit: 'day', durationMs: 86400000 },
      { unit: 'hour', durationMs: 3600000 },
    ];

    const startTime = this.toUtc(startDate) as number;
    const endTime = this.toUtc(endDate) as number;

    if (isNaN(startTime) || isNaN(endTime)) {
      return {
        recommended: 'day',
        available: this.timeUnits.map((u: any) => u.unit),
      };
    }

    const diffMs = Math.abs(endTime - startTime);
    if (diffMs === 0) {
      return { recommended: 'hour', available: ['hour'] };
    }

    const oneDay = 86400000;
    const sevenDays = oneDay * 7;
    const oneMonth = 2629800000;
    const threeMonths = oneMonth * 3;
    const sixMonths = oneMonth * 6;
    const oneYear = oneDay * 365;

    let recommended = 'day';
    let available: string[] = ['day'];

    if (diffMs <= oneDay) {
      recommended = 'hour';
      available = ['hour'];
    } else if (diffMs <= sevenDays) {
      recommended = 'day';
      available = ['day', 'hour'];
    } else if (diffMs <= oneMonth) {
      recommended = 'week';
      available = ['day', 'week'];
    } else if (diffMs <= threeMonths) {
      recommended = 'month';
      available = ['month', 'week'];
    } else if (diffMs <= sixMonths) {
      recommended = 'month';
      available = ['month', 'week'];
    } else if (diffMs <= oneYear) {
      recommended = 'month';
      available = ['month', 'quarter'];
    } else {
      recommended = 'year';
      available = ['month', 'year'];
    }

    return { recommended, available };
  }

  public selectedUnitName = 'Day';
  public selectedUnitIndex = 1;

  private emitValue(selectPrevious?: number, overwriteUnit?: string): void {
    const range = this.selectedRange();
    const comparison = this.comparisonRange();

    const value: DateRangePickerValue = {
      startDate: range.start,
      endDate: range.end,
      unit: this.timeUnit(),
      comparisonType: this.comparisonType(),
      comparisonStartDate: comparison?.start,
      comparisonEndDate: comparison?.end,
    };

    const suggestions = this.getUnitSuggestions(value.startDate, value.endDate);
    const calculatedUnit = suggestions.recommended;
    const unitIndex = this.timeUnits.findIndex(
      (u) => u.value === calculatedUnit
    );
    if (unitIndex !== -1) {
      this.selectedUnitName = this.timeUnits[unitIndex].label;
      this.selectedUnitIndex = unitIndex;
    }

    const allowedUnits = suggestions.available;
    const finalIndexes: number[] = [];
    let effectiveAllowed = allowedUnits;
    if (this.allowedGroupByUnits && this.allowedGroupByUnits.length > 0) {
      const allowedSet = new Set(this.allowedGroupByUnits);
      effectiveAllowed = allowedUnits.filter((u) =>
        allowedSet.has(u as TimeUnit)
      );
    }
    if (this.alwaysAvailableUnit) {
      if (Array.isArray(this.alwaysAvailableUnit)) {
        this.alwaysAvailableUnit.forEach((u) => {
          if (!effectiveAllowed.includes(u)) {
            effectiveAllowed = [...effectiveAllowed, u];
          }
        });
      } else {
        if (!effectiveAllowed.includes(this.alwaysAvailableUnit as TimeUnit)) {
          effectiveAllowed = [
            ...effectiveAllowed,
            this.alwaysAvailableUnit as TimeUnit,
          ];
        }
      }
    }
    this.timeUnits.forEach((unit, index) => {
      if (effectiveAllowed.includes(unit.value)) {
        finalIndexes.push(index);
      }
    });
    this.availableUnitIndexes = finalIndexes;

    this.valueChange.emit(value);
  }

  formatDate(date: dayjs.Dayjs): string {
    return date.format('MMM D, YYYY');
  }

  public configureGroupByUnits(
    units: TimeUnit[] | null,
    alwaysAvailable: TimeUnit | null = null
  ): void {
    this.allowedGroupByUnits = units;
    this.alwaysAvailableUnit = alwaysAvailable;
  }

  private filterUnits(
    base: Array<{ label: string; value: TimeUnit; icon: IconName }>
  ): Array<{ label: string; value: TimeUnit; icon: IconName }> {
    let filtered = base;
    if (this.allowedGroupByUnits && this.allowedGroupByUnits.length > 0) {
      const allowedSet = new Set(this.allowedGroupByUnits);
      filtered = base.filter((u) => allowedSet.has(u.value));
    }
    if (this.alwaysAvailableUnit) {
      const exists = filtered.some((u) => u.value === this.alwaysAvailableUnit);
      if (!exists) {
        const found = base.find((u) => u.value === this.alwaysAvailableUnit);
        if (found) {
          filtered = [...filtered, found];
        } else {
          const unitMap: Record<TimeUnit, { label: string; icon: IconName }> = {
            hour: { label: 'Hour', icon: 'calendar' },
            day: { label: 'Day', icon: 'calendar' },
            week: { label: 'Week', icon: 'calendar' },
            month: { label: 'Month', icon: 'calendar' },
            quarter: { label: 'Quarter', icon: 'chartNoAxesCombined' },
            year: { label: 'Year', icon: 'chartNoAxesCombined' },
          };
          if (Array.isArray(this.alwaysAvailableUnit)) {
            this.alwaysAvailableUnit.forEach((u) => {
              const meta = unitMap[u];
              filtered = [
                ...filtered,
                { label: meta.label, value: u, icon: meta.icon },
              ];
            });
          } else {
            const meta = unitMap[this.alwaysAvailableUnit];
            filtered = [
              ...filtered,
              {
                label: meta.label,
                value: this.alwaysAvailableUnit,
                icon: meta.icon,
              },
            ];
          }
        }
      }
    }
    const seen = new Set<string>();
    filtered = filtered.filter((u) => {
      if (seen.has(u.value)) return false;
      seen.add(u.value);
      return true;
    });
    return filtered;
  }

  private clampToToday(parsed: dayjs.Dayjs): dayjs.Dayjs {
    const todayLA = nowInLA().startOf('day');
    return parsed.isAfter(todayLA, 'day') ? todayLA : parsed;
  }

  formatInputDate(date: dayjs.Dayjs): string {
    return date.tz(LA_TIMEZONE).format('MM-DD-YYYY');
  }

  public autoFormatStartDate(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    if (!inputElement) return;

    let input = inputElement.value.replace(/\D/g, '');
    if (input.length > 8) input = input.slice(0, 8);
    if (input.length >= 4) {
      input = `${input.slice(0, 2)}-${input.slice(2, 4)}-${input.slice(4)}`;
    } else if (input.length >= 2) {
      input = `${input.slice(0, 2)}-${input.slice(2)}`;
    }

    inputElement.value = input;
    if (input.length === 10) {
      const parsed = dayjs.tz(input, 'MM-DD-YYYY', LA_TIMEZONE);
      if (parsed.isValid()) {
        const todayLA = nowInLA().startOf('day');
        const normalizedDate = parsed.startOf('day');
        if (normalizedDate.isAfter(todayLA, 'day')) {
          inputElement.value = this.formatInputDate(todayLA);
        }
      }
    }
  }

  public autoFormatEndDate(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    if (!inputElement) return;

    let input = inputElement.value.replace(/\D/g, '');
    if (input.length > 8) input = input.slice(0, 8);

    if (input.length >= 4) {
      input = `${input.slice(0, 2)}-${input.slice(2, 4)}-${input.slice(4)}`;
    } else if (input.length >= 2) {
      input = `${input.slice(0, 2)}-${input.slice(2)}`;
    }

    inputElement.value = input;

    if (input.length === 10) {
      const parsed = dayjs.tz(input, 'MM-DD-YYYY', LA_TIMEZONE);
      if (parsed.isValid()) {
        const todayLA = nowInLA().startOf('day');
        const normalizedDate = parsed.startOf('day');
        if (normalizedDate.isAfter(todayLA, 'day')) {
          inputElement.value = this.formatInputDate(todayLA);
        }
      }
    }
  }

  onStartDateChange(value: string): void {
    const parsed = dayjs.tz(value, 'MM-DD-YYYY', LA_TIMEZONE);
    if (!parsed.isValid()) return;

    const todayLA = nowInLA().startOf('day');
    const normalizedDate = parsed.startOf('day');
    const yesterdayLA = todayLA.subtract(1, 'day');

    let newStart: dayjs.Dayjs;
    let newEnd: dayjs.Dayjs;

    if (normalizedDate.isSame(todayLA, 'day')) {
      newStart = yesterdayLA;
      newEnd = todayLA;
    } else {
      const currentEnd = dayjs
        .utc(this.selectedRange().end)
        .tz(LA_TIMEZONE)
        .startOf('day');
      const effectiveEnd = currentEnd.isBefore(normalizedDate, 'day')
        ? normalizedDate
        : currentEnd;
      newStart = normalizedDate;
      newEnd = effectiveEnd;
    }

    const newRange: DateRange = {
      start: newStart.utc().toISOString(),
      end: newEnd.utc().toISOString(),
    };

    this.selectedRange.set(newRange);
    this.temporaryRange.set(newRange);
    this.updateCalendarMonths(newStart, newEnd);
  }

  onEndDateChange(value: string): void {
    const parsed = dayjs.tz(value, 'MM-DD-YYYY', LA_TIMEZONE);
    if (!parsed.isValid()) return;

    const todayLA = nowInLA().startOf('day');
    const normalizedDate = parsed.startOf('day');
    const yesterdayLA = todayLA.subtract(1, 'day');
    const currentStart = dayjs
      .utc(this.selectedRange().start)
      .tz(LA_TIMEZONE)
      .startOf('day');

    let newStart: dayjs.Dayjs;
    let newEnd: dayjs.Dayjs;

    const dayDiff = normalizedDate.diff(currentStart, 'day');

    if (normalizedDate.isSame(todayLA, 'day') && dayDiff <= 1) {
      newStart = yesterdayLA;
      newEnd = todayLA;
    } else {
      newStart = currentStart;
      newEnd = normalizedDate;
    }

    const newRange: DateRange = {
      start: newStart.utc().toISOString(),
      end: newEnd.utc().toISOString(),
    };

    this.selectedRange.set(newRange);
    this.temporaryRange.set(newRange);
    this.updateCalendarMonths(newStart, newEnd);
  }

  onComparisonStartDateChange(value: string): void {
    try {
      const parsed = dayjs.tz(value, 'MM-DD-YYYY', LA_TIMEZONE);
      if (!parsed.isValid()) return;

      const todayLA = nowInLA().startOf('day');
      const normalizedDate = parsed.startOf('day');

      if (normalizedDate.isAfter(todayLA, 'day')) return;

      const existingEnd = this.comparisonRange()
        ? dayjs.utc(this.comparisonRange()!.end).tz(LA_TIMEZONE).startOf('day')
        : normalizedDate;

      const effectiveEnd = existingEnd.isBefore(normalizedDate, 'day')
        ? normalizedDate
        : existingEnd;

      this.setComparisonRange({
        start: normalizedDate.utc().toISOString(),
        end: effectiveEnd.utc().toISOString(),
      });
    } catch (error) {
      console.error(error);
    }
  }

  onComparisonEndDateChange(value: string): void {
    try {
      const parsed = dayjs.tz(value, 'MM-DD-YYYY', LA_TIMEZONE);
      if (!parsed.isValid()) return;

      const todayLA = nowInLA().startOf('day');
      const normalizedDate = parsed.startOf('day');

      if (normalizedDate.isAfter(todayLA, 'day')) return;

      const existingStart = this.comparisonRange()
        ? dayjs
            .utc(this.comparisonRange()!.start)
            .tz(LA_TIMEZONE)
            .startOf('day')
        : normalizedDate;

      const effectiveStart = existingStart.isAfter(normalizedDate, 'day')
        ? normalizedDate
        : existingStart;

      this.setComparisonRange({
        start: effectiveStart.utc().toISOString(),
        end: normalizedDate.utc().toISOString(),
      });
    } catch (error) {
      console.error(error);
    }
  }

  formatDateInLA(isoString: string): string {
    return dayjs.utc(isoString).tz(LA_TIMEZONE).format('MMM D, YYYY');
  }

  getDaysDifference(start: dayjs.Dayjs, end: dayjs.Dayjs): number {
    return end.diff(start, 'day') + 1;
  }

  trackByDate(index: number, day: CalendarDay): string {
    return day.date.format('YYYY-MM-DD');
  }

  getYearOptions(): number[] {
    const currentYear = dayjs().year();
    return Array.from({ length: 9 }, (_, i) => currentYear - 8 + i).reverse();
  }

  dayjs = dayjs;

  getDayClasses(day: CalendarDay): string {
    const classes = [];
    if (day.isDisabled || !day.isCurrentMonth) {
      classes.push('opacity-25 cursor-not-allowed');
      if (!day.isCurrentMonth) {
        classes.push('text-gray-400 dark:text-gray-600');
        return classes.join(' ');
      }
    } else {
      classes.push(
        'cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600/50'
      );
    }

    if (day.isCurrentMonth) {
      classes.push('text-gray-900 dark:text-white');
      if (day.isToday && !day.isSelected) {
        classes.push('text-purple-700 dark:text-purple-300 font-medium');
      }

      if (day.isStartDate || day.isEndDate) {
        classes.push(
          'bg-gradient-to-br from-purple-600 to-indigo-600 text-white shadow-lg scale-110 z-10'
        );
      } else if (day.isInRange) {
        classes.push(
          'bg-purple-100 dark:bg-purple-500/20 text-purple-700 dark:text-purple-200'
        );
      }

      if (day.isComparisonStart || day.isComparisonEnd) {
        classes.push(
          'bg-gradient-to-br from-purple-600 to-indigo-600 text-white font-medium'
        );
      } else if (day.isComparisonRange) {
        classes.push(
          'bg-purple-100 dark:bg-purple-500/20 text-purple-700 dark:text-purple-200'
        );
      }
    }
    return classes.join(' ');
  }

  readonly MIN_DATE = dayjs.utc('2017-01-01');
  readonly MAX_DATE = nowInLA();

  isPrevDisabled(): boolean {
    return this.startCalendarMonth().isSameOrBefore(this.MIN_DATE, 'month');
  }

  isNextDisabled(): boolean {
    return this.endCalendarMonth().isSameOrAfter(this.MAX_DATE, 'month');
  }

  isOpen = false;
  isYearOpen = false;
  isNextMonthOpen = false;
  isNextYearOpen = false;

  toggleDropdown() {
    this.isOpen = !this.isOpen;
  }

  setMonthAndClose(monthIndex: number) {
    this.setCalendarMonth(monthIndex, this.startCalendarMonth().year());
    this.isOpen = false;
  }

  setYearAndClose(year: number) {
    this.setCalendarMonth(this.startCalendarMonth().month(), year);
    this.isYearOpen = false;
  }

  endMonthOpen = false;
  endYearOpen = false;

  setEndMonthAndClose(monthIndex: number) {
    const proposedEndCalendar = nowInLA()
      .year(this.endCalendarMonth().year())
      .month(monthIndex)
      .startOf('month');

    if (
      proposedEndCalendar.isSameOrBefore(this.startCalendarMonth(), 'month')
    ) {
      this.startCalendarMonth.set(proposedEndCalendar.subtract(1, 'month'));
    }

    this.setCalendarMonth(monthIndex, this.endCalendarMonth().year(), true);
    this.isEndMonthOpen.set(false);
  }

  setEndYearAndClose(year: number) {
    const proposedEndCalendar = nowInLA()
      .year(year)
      .month(this.endCalendarMonth().month())
      .startOf('month');

    if (
      proposedEndCalendar.isSameOrBefore(this.startCalendarMonth(), 'month')
    ) {
      this.startCalendarMonth.set(proposedEndCalendar.subtract(1, 'month'));
    }

    this.setCalendarMonth(this.endCalendarMonth().month(), year, true);
    this.isEndYearOpen.set(false);
  }

  endYear = this.endCalendarMonth().year();
  endMonth = this.endCalendarMonth().month();

  isStartMonthValid(monthIndex: number): boolean {
    const proposedStartCalendar = nowInLA()
      .year(this.startCalendarMonth().year())
      .month(monthIndex)
      .startOf('month');
    const today = nowInLA().startOf('month');
    return proposedStartCalendar.isSameOrBefore(today, 'month');
  }

  isStartYearValid(year: number): boolean {
    const proposedStartCalendar = nowInLA()
      .year(year)
      .month(this.startCalendarMonth().month())
      .startOf('month');
    const today = nowInLA().startOf('month');
    return proposedStartCalendar.isSameOrBefore(today, 'month');
  }

  isEndMonthValid(monthIndex: number): boolean {
    const proposedEndCalendar = nowInLA()
      .year(this.endCalendarMonth().year())
      .month(monthIndex)
      .startOf('month');
    const today = nowInLA().startOf('month');

    const isAfterStart = proposedEndCalendar.isAfter(
      this.startCalendarMonth(),
      'month'
    );
    const isNotInFuture = proposedEndCalendar.isSameOrBefore(today, 'month');

    return isAfterStart && isNotInFuture;
  }

  isEndYearValid(year: number): boolean {
    const proposedEndCalendar = nowInLA()
      .year(year)
      .month(this.endCalendarMonth().month())
      .startOf('month');
    const today = nowInLA().startOf('month');

    const isAfterStart = proposedEndCalendar.isAfter(
      this.startCalendarMonth(),
      'month'
    );
    const isNotInFuture = proposedEndCalendar.isSameOrBefore(today, 'month');

    return isAfterStart && isNotInFuture;
  }

  onEndYearChange(year: number) {
    this.setCalendarMonth(this.endMonth, year, true);
  }

  onEndMonthChange(month: number) {
    this.setCalendarMonth(month, this.endYear, true);
  }

  // ========== Mobile Helpers ==========

  /** Active preset label for the mobile preset selector. */
  activePresetLabel = computed(() => {
    const type = this.selectedType();
    return type ? type : 'Tap to Select';
  });

  /** Group-by options in mobile dropdown, with disabled flag based on allowed units. */
  groupByOptions = computed(() => {
    const allowed = new Set<TimeUnit>(this.allowedGroupByUnits ?? []);
    if (this.alwaysAvailableUnit) {
      if (Array.isArray(this.alwaysAvailableUnit)) {
        this.alwaysAvailableUnit.forEach((u) => allowed.add(u));
      } else {
        allowed.add(this.alwaysAvailableUnit);
      }
    }
    const hasRestriction = allowed.size > 0;
    return this.timeUnits.map((u) => ({
      label: u.label,
      value: u.value,
      icon: u.icon,
      disabled: hasRestriction && !allowed.has(u.value),
    }));
  });

  selectedGroupByLabel = computed(() => {
    const found = this.timeUnits.find((u) => u.value === this.timeUnit());
    return found ? found.label : 'Choose Group Type';
  });

  /** Display text for the start month/year card on mobile. */
  mobileStartDisplay = computed(() => {
    // Always read the range signals first so this computed stays
    // subscribed to them across re-evaluations. Without this, the
    // early-return below (after a side is first committed) would
    // strip the dependency and freeze the card on its initial value,
    // because `selectedStartDate` is a plain field — not a signal —
    // and overwriting it cannot trigger a recomputation.
    const range = this.temporaryRange() || this.selectedRange();
    if (this.selectedStartDate) {
      return dayjs()
        .year(this.selectedStartDate.year)
        .month(this.selectedStartDate.month - 1)
        .format('MMM YYYY');
    }
    return dayjs.utc(range.start).tz(LA_TIMEZONE).format('MMM YYYY');
  });

  /** Display text for the end month/year card on mobile. */
  mobileEndDisplay = computed(() => {
    // See mobileStartDisplay — same dependency-tracking rationale.
    const range = this.temporaryRange() || this.selectedRange();
    if (this.selectedEndDate) {
      return dayjs()
        .year(this.selectedEndDate.year)
        .month(this.selectedEndDate.month - 1)
        .format('MMM YYYY');
    }
    return dayjs.utc(range.end).tz(LA_TIMEZONE).format('MMM YYYY');
  });

  toggleMobilePreset(): void {
    this.isMobilePresetOpen.update((v) => !v);
  }

  toggleMobileGroupBy(): void {
    this.isMobileGroupByOpen.update((v) => !v);
  }

  setMobileView(view: 'year' | 'month'): void {
    this.mobileView.set(view);
  }

  getPresetId(label: string): string {
    return label.toLowerCase().replace(/\s+/g, '');
  }

  /** Navigate the calendar by year on mobile (since the picker is month/year only). */
  changeMobileYear(delta: number): void {
    this.mobileBrowseDate.set(this.mobileBrowseDate().add(delta, 'year'));
  }

  /** Switch between Start/End selection and align the calendar with the
   *  active side's currently selected (or previously displayed) year/month.
   *  Decoupling navigation per side fixes the bug where picking End
   *  year/month silently flipped the Start side because both shared a
   *  single browse position.
   */
  onMobileSideToggle(start: boolean): void {
    this.isSelectingStart.set(start);
    const range = this.temporaryRange() || this.selectedRange();
    const fallbackIso = start ? range.start : range.end;
    const fallback = dayjs.utc(fallbackIso).tz(LA_TIMEZONE);
    const committed = start ? this.selectedStartDate : this.selectedEndDate;
    const target = committed
      ? nowInLA().year(committed.year).month(committed.month - 1)
      : fallback;
    this.mobileBrowseDate.set(target.startOf('month'));
  }

  /** Highlight target for the month grid — the active side's currently
   *  picked month, or the matching value from the displayed range, but
   *  only when the browsed year actually matches so we don't visually
   *  imply a selection on a year the user is just scrolling through.
   */
  mobileHighlightedMonth = computed<number | null>(() => {
    // Read all signal dependencies up-front so the computed always
    // re-runs when the range changes — `selectedStartDate`/`End` are
    // plain fields and cannot trigger invalidation on their own.
    const browseYear = this.mobileBrowseDate().year();
    const selectingStart = this.isSelectingStart();
    const range = this.temporaryRange() || this.selectedRange();
    const committed = selectingStart
      ? this.selectedStartDate
      : this.selectedEndDate;
    if (committed) {
      return committed.year === browseYear ? committed.month - 1 : null;
    }
    const iso = selectingStart ? range.start : range.end;
    const d = dayjs.utc(iso).tz(LA_TIMEZONE);
    return d.year() === browseYear ? d.month() : null;
  });

  /** Highlight target for the year grid — the active side's currently
   *  picked year (falling back to the displayed range if nothing has
   *  been committed yet).
   */
  mobileHighlightedYear = computed<number | null>(() => {
    // Same dependency-tracking rationale as mobileHighlightedMonth.
    const selectingStart = this.isSelectingStart();
    const range = this.temporaryRange() || this.selectedRange();
    const committed = selectingStart
      ? this.selectedStartDate
      : this.selectedEndDate;
    if (committed) return committed.year;
    const iso = selectingStart ? range.start : range.end;
    return dayjs.utc(iso).tz(LA_TIMEZONE).year();
  });

  /** Effective lower bound (year + 1-based month) for the End side.
   *  Returns the committed Start when present, otherwise falls back to
   *  the start of the currently displayed range so we still block End
   *  picks earlier than wherever Start is anchored.
   */
  private getMobileEndLowerBound(): { year: number; month: number } | null {
    if (this.selectedStartDate) return this.selectedStartDate;
    const range = this.temporaryRange() || this.selectedRange();
    const d = dayjs.utc(range.start).tz(LA_TIMEZONE);
    return { year: d.year(), month: d.month() + 1 };
  }

  /** True when a {year, month} pair is strictly before another. */
  private isBeforeYearMonth(
    a: { year: number; month: number },
    b: { year: number; month: number }
  ): boolean {
    return a.year < b.year || (a.year === b.year && a.month < b.month);
  }

  /** Whether the proposed mobile month is selectable (not in the future, etc.). */
  isMobileMonthDisabled(monthIndex: number): boolean {
    const proposed = nowInLA()
      .year(this.mobileBrowseDate().year())
      .month(monthIndex)
      .startOf('month');
    const today = nowInLA().startOf('month');
    if (proposed.isAfter(today, 'month')) return true;
    if (
      this.minDate &&
      proposed.isBefore(dayjs.utc(this.minDate), 'month')
    ) {
      return true;
    }
    // Block End picks that fall before the Start anchor so the range
    // can never be inverted.
    if (!this.isSelectingStart()) {
      const lower = this.getMobileEndLowerBound();
      if (
        lower &&
        this.isBeforeYearMonth(
          { year: this.mobileBrowseDate().year(), month: monthIndex + 1 },
          lower
        )
      ) {
        return true;
      }
    }
    return false;
  }

  /** Whether the proposed mobile year is selectable. */
  isMobileYearDisabled(year: number): boolean {
    const today = nowInLA().startOf('year');
    const proposed = nowInLA().year(year).startOf('year');
    if (proposed.isAfter(today, 'year')) return true;
    // For the End side, prevent navigating to a year strictly before
    // the Start anchor. Same-year is allowed; per-month invalidation
    // is handled by isMobileMonthDisabled.
    if (!this.isSelectingStart()) {
      const lower = this.getMobileEndLowerBound();
      if (lower && year < lower.year) return true;
    }
    return false;
  }

  /** Picks a month in mobile view, updating selectedStart/End based on isSelectingStart. */
  selectMobileMonth(index: number): void {
    if (this.isMobileMonthDisabled(index)) return;

    const year = this.mobileBrowseDate().year();
    const month = index + 1;

    // Hard guard: End must never land before Start. The disable state
    // already prevents this in normal flows, but this keeps the picker
    // from inverting the range if a stale/duplicate event sneaks
    // through.
    if (!this.isSelectingStart()) {
      const lower = this.getMobileEndLowerBound();
      if (lower && this.isBeforeYearMonth({ year, month }, lower)) return;
    }

    // Keep the browse position in sync with the just-picked month so
    // the highlight stays anchored to what the user just tapped.
    this.mobileBrowseDate.set(
      this.mobileBrowseDate().month(index).startOf('month')
    );

    // Capture the previously displayed range so we can preserve the
    // OTHER side when only one ends up committed (avoids the single-
    // month preview silently flipping the other card to match).
    const previousRange = this.temporaryRange() || this.selectedRange();
    const previousEndIso = previousRange.end;
    const previousStartIso = previousRange.start;

    // The active side is controlled exclusively by the Start/End cards.
    // We never auto-toggle here, so re-picking a year/month for the
    // same side keeps editing that side instead of silently jumping
    // to the other card.
    if (this.isSelectingStart()) {
      this.onDateRangeFilter({ year, month }, 'start');
    } else {
      this.onDateRangeFilter({ year, month }, 'end');
    }

    // User has performed a custom month pick — mark the session so the
    // apply output isn't tagged with a stale preset label.
    this.customRangeActive = true;

    if (this.selectedStartDate && this.selectedEndDate) {
      this.handleMainDateClickPublic();
    } else if (this.selectedStartDate) {
      const start = dayjs
        .tz(
          `${this.selectedStartDate.year}-${String(
            this.selectedStartDate.month
          ).padStart(2, '0')}-01`,
          LA_TIMEZONE
        )
        .startOf('month')
        .utc();
      const previousEnd = dayjs.utc(previousEndIso);
      const endIso = previousEnd.isBefore(start, 'month')
        ? start.endOf('month').toISOString()
        : previousEndIso;
      const tempRange: DateRange = {
        start: start.toISOString(),
        end: endIso,
      };
      this.temporaryRange.set(tempRange);
      this.selectedRange.set(tempRange);
    } else if (this.selectedEndDate) {
      const end = dayjs
        .tz(
          `${this.selectedEndDate.year}-${String(
            this.selectedEndDate.month
          ).padStart(2, '0')}-01`,
          LA_TIMEZONE
        )
        .endOf('month')
        .utc();
      const previousStart = dayjs.utc(previousStartIso);
      const startIso = previousStart.isAfter(end, 'month')
        ? end.startOf('month').toISOString()
        : previousStartIso;
      const tempRange: DateRange = {
        start: startIso,
        end: end.toISOString(),
      };
      this.temporaryRange.set(tempRange);
      this.selectedRange.set(tempRange);
    }
  }

  /** Picks a year in mobile year-view; switches to month view afterwards. */
  selectMobileYear(year: number): void {
    if (this.isMobileYearDisabled(year)) return;
    this.mobileBrowseDate.set(
      this.mobileBrowseDate().year(year).startOf('month')
    );

    // Mobile picks (year + month) as a unit. Propagate the year change
    // to the active side (start or end) so its card and the resulting
    // range reflect the new year immediately. If the side has not been
    // explicitly committed yet, derive its month from the currently
    // displayed range.
    //
    // CRITICALLY, when updating just one side we must preserve the
    // OTHER side of the displayed range. Otherwise the temp-range
    // rebuild would flip the other card to the same year/month and the
    // user perceives "my year went to the wrong side".
    const displayedRange = this.temporaryRange() || this.selectedRange();
    const previousStartIso = displayedRange.start;
    const previousEndIso = displayedRange.end;

    if (this.isSelectingStart()) {
      const month =
        this.selectedStartDate?.month ??
        (dayjs.utc(previousStartIso).tz(LA_TIMEZONE).month() + 1);
      this.onDateRangeFilter({ year, month }, 'start');
    } else {
      const month =
        this.selectedEndDate?.month ??
        (dayjs.utc(previousEndIso).tz(LA_TIMEZONE).month() + 1);
      const proposedEnd = { year, month };
      if (
        this.selectedStartDate &&
        (proposedEnd.year < this.selectedStartDate.year ||
          (proposedEnd.year === this.selectedStartDate.year &&
            proposedEnd.month < this.selectedStartDate.month))
      ) {
        // New end falls before the existing start; snap to the start
        // month so the range stays valid instead of being inverted.
        this.onDateRangeFilter(
          {
            year: this.selectedStartDate.year,
            month: this.selectedStartDate.month,
          },
          'end'
        );
      } else {
        this.onDateRangeFilter(proposedEnd, 'end');
      }
    }

    // User has performed a custom year pick — mark the session so the
    // apply output isn't tagged with a stale preset label.
    this.customRangeActive = true;

    if (this.selectedStartDate && this.selectedEndDate) {
      this.handleMainDateClickPublic();
    } else if (this.selectedStartDate) {
      // Only start has been committed. Keep the previous end ISO so the
      // End card still shows the user's previous end value rather than
      // collapsing to a single-month preview. If the previous end now
      // falls before the new start, fall back to end-of-start-month so
      // the range stays valid.
      const start = dayjs
        .tz(
          `${this.selectedStartDate.year}-${String(
            this.selectedStartDate.month
          ).padStart(2, '0')}-01`,
          LA_TIMEZONE
        )
        .startOf('month')
        .utc();
      const previousEnd = dayjs.utc(previousEndIso);
      const endIso = previousEnd.isBefore(start, 'month')
        ? start.endOf('month').toISOString()
        : previousEndIso;
      const newRange: DateRange = {
        start: start.toISOString(),
        end: endIso,
      };
      this.temporaryRange.set(newRange);
      this.selectedRange.set(newRange);
    } else if (this.selectedEndDate) {
      // Only end has been committed. Keep the previous start ISO. If
      // the previous start now falls after the new end, fall back to
      // start-of-end-month so the range stays valid.
      const end = dayjs
        .tz(
          `${this.selectedEndDate.year}-${String(
            this.selectedEndDate.month
          ).padStart(2, '0')}-01`,
          LA_TIMEZONE
        )
        .endOf('month')
        .utc();
      const previousStart = dayjs.utc(previousStartIso);
      const startIso = previousStart.isAfter(end, 'month')
        ? end.startOf('month').toISOString()
        : previousStartIso;
      const newRange: DateRange = {
        start: startIso,
        end: end.toISOString(),
      };
      this.temporaryRange.set(newRange);
      this.selectedRange.set(newRange);
    }

    this.mobileView.set('month');
  }

  /** Mobile preset click handler - selects the preset and closes the dropdown. */
  onMobilePresetClick(preset: { label: string; preset: string }): void {
    this.isMobilePresetOpen.set(false);

    const range = this.predefinedRangesList.find(
      (r) => this.getPresetId(r.label) === preset.preset.toLowerCase());
    if (range) {
      this.selectedStartDate = null;
      this.selectedEndDate = null;
      // Picking a preset cancels any in-progress custom selection.
      this.customRangeActive = false;
      this.selectedType.set(range.label);
      const value = range.getValue();
      this.selectedRange.set(value);
      this.selectedPreset.set('custom');
      this.updateCalendarMonths(dayjs.tz(value.start, LA_TIMEZONE));
      // Anchor the mobile browse position to the active side (defaults
      // to Start) so the calendar header/highlights reflect the preset.
      this.mobileBrowseDate.set(
        dayjs.utc(value.start).tz(LA_TIMEZONE).startOf('month')
      );
      this.autoSelectTimeUnitMobile();
      this.updateComparison();
    }
  }

  /** Reset to default state on mobile. */
  clearMobile(): void {
    this.selectedStartDate = null;
    this.selectedEndDate = null;
    // Clearing wipes any in-progress custom selection too.
    this.customRangeActive = false;
    this.timeUnit.set(this.getUnit);
    this.comparisonType.set('none');
    this.mobileView.set('month');
    this.isMobilePresetOpen.set(false);
    this.isMobileGroupByOpen.set(false);
    this.isSelectingStart.set(true);
    this.mobileBrowseDate.set(nowInLA().startOf('month'));
    this.temporaryRange.set(null);
    this.originalRange.set(null);

    const defaultRange =
      this.predefinedRangesList.find((r) => r.label === 'Last 3 Months') ??
      this.predefinedRangesList[0];
    if (defaultRange) {
      this.selectedType.set(defaultRange.label);
      this.selectedRange.set(defaultRange.getValue());
    } else {
      this.selectedType.set('');
    }
  }
  /** Public wrapper used by mobile to recompute the range from selectedStart/End. */
  public handleMainDateClickPublic(): void {
    if (this.selectedStartDate && this.selectedEndDate) {
      const start = dayjs
        .tz(
          `${this.selectedStartDate.year}-${String(
            this.selectedStartDate.month
          ).padStart(2, '0')}-01`,
          LA_TIMEZONE
        )
        .startOf('month')
        .utc();

      const end = dayjs
        .tz(
          `${this.selectedEndDate.year}-${String(
            this.selectedEndDate.month
          ).padStart(2, '0')}-01`,
          LA_TIMEZONE
        )
        .endOf('month')
        .utc();

      const tempRange = {
        start: start.toISOString(),
        end: end.toISOString(),
      };

      this.temporaryRange.set(tempRange);
      this.selectedRange.set(tempRange);
    }
  }

  private autoSelectTimeUnitMobile(): void {
    this.autoSelectTimeUnit();
  }

  /** Build the output emitted on apply (used on mobile sheet dismiss). */
  private buildDateRangeOutput(): any {
    if (this.selectedStartDate && this.selectedEndDate) {
      this.handleMainDateClickPublic();
    }
    const range = this.selectedRange();
    const unit: string = this.timeUnit();

    // When the user has just performed a custom Start/End pick on
    // mobile, force the preset label to empty. `setMatchingPreset`'s
    // hint fallback (kept around so relative ranges like "Last 3 Months"
    // survive midnight/DST drift) would otherwise leave `selectedType`
    // pointing at the previous preset, and hosts that key off
    // `preset_label === '<default preset>'` would misread a fresh
    // custom apply as a "reset" and discard the user's selection.
    const presetLabel = this.customRangeActive ? '' : this.selectedType();

    return {
      start_date: range.start,
      end_date: range.end,
      unit,
      preset_label: presetLabel,
      previous_start_date: this.comparisonRange()?.start ?? null,
      previous_end_date: this.comparisonRange()?.end ?? null,
    };
  }
}